import React, { useEffect, useState } from "react";
import { View, Pressable } from "react-native-web";
import { useParams, useLocation } from "react-router-dom";
import CustomText from "../../../../components/atoms/CustomText";
import { colors, spacing } from "../../../../utils/styling";
import { useResponsive } from "../../../../hooks/useResponsive";
import ResultsGraph from "../../../../components/common/ResultsGraph";
import CatContributionBar from "../../../../components/common/CatContributionBar";
import {
  resolveCoreSection,
  resolveWorkbookExercises,
} from "../../../../utils/quizinfo/personalityContent";
import { useAccountQuizzes, useProfileQuizzes } from "../../../../lib/quizzes";
import BaseModal from "../../../../components/popups/BaseModal";
import ExerciseRenderer from "../../../../components/common/ExerciseRenderer";
import { useCurrentUserExercises } from "../../../../lib/profiles";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PersonalityTestPDF from "../../../../components/common/PersonalityTestPDF";
import { QUESTIONS } from "../../../../utils/quizinfo/questions";
import {
  resolveStoredAnswers,
  toRenderableIndices,
} from "../../../../utils/quizinfo/legacyAnswers";
import SvgClipboard from "../../../../components/icons/SvgClipboard";
import SendQuizModal from "../../../../components/popups/SendQuizModal";
import SvgBrain from "../../../../components/icons/SvgBrain";
import SvgDrawingCompass from "../../../../components/icons/SvgDrawingCompass";
import SvgTools from "../../../../components/icons/SvgTools";
import SvgKeys from "../../../../components/icons/SvgKeys";
import { useCurrentProfile } from "../../../../lib/profiles";
import { useNavigate } from "react-router-dom";
import { migrateDatabaseQuizResults } from "../../../../utils/categoryMigration";
import { loadDataWithVersionCheck } from "../../../../utils/versionManager";
import { formatQuizDate } from "../../../../utils/quizDate";

const CATEGORY_COLORS: Record<string, string> = {
  "Dream Chaser": "#00A4EF",
  "Wealth Architect": "#00B3C2",
  "Safety Seeker": "#076652",
  "Generous Altruist": "#9933FF",
  "Freedom Finder": "#FF00FF",
};


// One color per activity box in the Exercises tab, cycling: the five personality
// colors, so the boxes read as part of the same palette as the rest of the site.
const EXERCISE_BOX_COLORS = ["#00A4EF", "#9933FF", "#00B3C2", "#FF00FF", "#076652"];

type ResultsType = Record<string, number>;

function getDominantCategory(results: ResultsType): string {
  if (!results || Object.entries(results).length === 0) {
    return "Freedom Finder";
  }
  return Object.entries(results).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
}


// Function to get exercise type and icon based on exercise ID
function getExerciseTypeAndIcon(id: number) {
  switch (id) {
    case 1:
      return { type: "Reflective", icon: SvgBrain, color: "#000000" };
    case 2:
      return {
        type: "Visualization/Mapping",
        icon: SvgDrawingCompass,
        color: "#000000",
      };
    case 3:
      return { type: "Action Planning", icon: SvgTools, color: "#000000" };
    case 4:
      return {
        type: "Behavior/Pattern Tracking",
        icon: SvgClipboard,
        color: "#000000",
      };
    case 5:
      return {
        type: "Interactive Insight Activity",
        icon: SvgKeys,
        color: "#000000",
      };
    default:
      return { type: "Exercise", icon: SvgBrain, color: "#000000" };
  }
}

export default function PersonalityTab({
  sfAccount,
  activeTab,
  userName,
  setActiveTab,
}: {
  sfAccount: string | undefined;
  activeTab: "results" | "exercises";
  userName?: string;
  setActiveTab?: (
    tab: "results" | "exercises" | "pdf" | "subscription"
  ) => void;
}) {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdvisorView = location.pathname.includes("/advisor/");

  // Bug 15 fix (2026-08-29): in advisor view, source quiz data from the
  // CLIENT's SF account, not the logged-in advisor's profile. OverviewTab
  // already does this correctly; PersonalityTab was the holdout.
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useCurrentProfile();
  const { data: accountQuizzes, isLoading: accountQuizzesLoading } = useAccountQuizzes(sfAccount as string);
  const { data: profileQuizzes, isLoading: profileQuizzesLoading } = useProfileQuizzes(
    isAdvisorView ? undefined : profile?.id
  );
  const quizzes = isAdvisorView ? accountQuizzes : profileQuizzes;
  const isLoading = isAdvisorView ? accountQuizzesLoading : profileQuizzesLoading;
  const personalInfo = isAdvisorView ? undefined : profile?.info;
  const [showDownload, setShowDownload] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [showSendQuizModal, setShowSendQuizModal] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);

  // Exercise state and internal tab state for advisor view
  const [selectedExercise, setSelectedExercise] = useState<any>(null);
  const [internalActiveTab, setInternalActiveTab] = useState<
    "results" | "exercises"
  >(activeTab);

  // Load exercises data
  const { data: exercisesData, isLoading: exercisesLoading } =
    useCurrentUserExercises();

  // Update internal tab when prop changes
  useEffect(() => {
    setInternalActiveTab(activeTab);
  }, [activeTab]);

  // Use internal tab state for advisor view, prop for client view
  const currentActiveTab: "results" | "exercises" = isAdvisorView
    ? internalActiveTab
    : activeTab;

  // Copy quiz link to clipboard
  const handleCopyQuizLink = async () => {
    if (!sfAccount) return;

    const quizLink = `${window.location.origin}/quiz/${sfAccount}`;

    try {
      await navigator.clipboard.writeText(quizLink);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000); // 3 seconds
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = quizLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000); // 3 seconds
    }
  };

  useEffect(() => {
    console.log("🧪 quizzes API response:", quizzes);
  }, [quizzes]);

  useEffect(() => {
    // Check if user has done the personal info collection
    if (personalInfo) {
      setShowDownload(true);
    } else {
      setShowDownload(false);
    }
  }, [personalInfo]);

  if (isLoading) {
    return <CustomText>Loading...</CustomText>;
  }
  if (!quizzes || quizzes.length === 0) {
    return (
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? spacing.md : spacing.xl,
          paddingTop: isMobile ? "100px" : 0,
          paddingLeft: isMobile ? "4px" : spacing.xl,
          paddingRight: isMobile ? "4px" : spacing.xl,
          marginBottom: spacing.xxl,
        }}
      >
        {/* Action Row - Buttons (Only show in advisor view when no quiz data) */}
        {isAdvisorView && (
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 16,
              marginTop: isMobile ? "0px" : "20px",
              marginBottom: 24,
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              maxWidth: 1400,
            }}
          >
            <Pressable
              onPress={() => setShowSendQuizModal(true)}
              style={{
                backgroundColor: colors.primary,
                paddingTop: "12px",
                paddingBottom: "12px",
                paddingLeft: "16px",
                paddingRight: "16px",
                borderRadius: 8,
                cursor: "pointer",
                border: "none",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <SvgClipboard color={colors.white} width={16} height={16} />
              <CustomText
                style={{
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: 16,
                  fontFamily: "Nunito",
                }}
              >
                Send Quiz
              </CustomText>
            </Pressable>
          </View>
        )}

        <SendQuizModal
          isVisible={showSendQuizModal}
          onClose={() => setShowSendQuizModal(false)}
          quizLink={`${window.location.origin}/quiz/${sfAccount || ''}`}
          clientName={userName}
        />

        <CustomText
          style={{
            fontSize: isMobile ? 24 : 32,
            color: colors.gray[600],
            textAlign: "center",
            marginTop: spacing.xl,
            fontFamily: "Nunito",
          }}
        >
          {isAdvisorView
            ? "Awaiting client response"
            : "No quiz data available."}
        </CustomText>
        {isAdvisorView && (
          <CustomText
            style={{
              fontSize: isMobile ? 16 : 18,
              color: colors.gray[600],
              textAlign: "center",
              marginTop: spacing.md,
              maxWidth: 560,
              fontFamily: "Nunito",
            }}
          >
            {userName ? `${userName} has` : "This client has"} not completed the
            Money Personality assessment yet. Copy the link above and send it to
            them — their results and the date they took it appear here once they
            finish.
          </CustomText>
        )}
      </View>
    );
  }

  // Use the most recent quiz
  const sortedQuizzes = [...quizzes].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  let activeQuiz = sortedQuizzes[0];

  // Apply migration to database quiz results
  activeQuiz = migrateDatabaseQuizResults(activeQuiz);

  const quizAnswers = Array.isArray(activeQuiz.answers?.quiz)
    ? activeQuiz.answers.quiz
    : [];
  // F17 (McKayla Loom NL1 t=2:12): "this needs to be their personality. So,
  // personality, what date they took it."
  const quizDateTaken = formatQuizDate(activeQuiz?.created_at);
  const mainResults = activeQuiz?.results?.mainQuiz || {};
  const specificResults = activeQuiz?.results?.specificQuiz || {};
  let dominantCategory = getDominantCategory(mainResults);
  const specificDominantCategory = getDominantCategory(specificResults);
  const categoryScores = mainResults;

  // Core-personality content, resolved defensively (ADR 2026-07-20). Until
  // 2026-08-20 this component indexed SPECIFIC_CATEGORY_SECTIONS[...] bare in
  // nine places; with the sub-quiz removed `specificDominantCategory` is "" for
  // every new result, so each of those would have thrown on the FIRST render
  // for FIRST-TIME clients — the exact white-screen the ADR exists to prevent.
  const coreSection = resolveCoreSection(dominantCategory, "PersonalityTab");

  // Workbook exercises are still keyed to the retired sub-types; see
  // utils/quizinfo/personalityContent.ts. Empty for any result created after
  // 2026-08-20, which the render site below states explicitly.
  const workbookExercises = resolveWorkbookExercises(specificDominantCategory, dominantCategory);

  // Get user info from profile data (preferred) or quiz data (fallback)
  const userInfo: Array<{
    question: string;
    answer: string;
    question_id: number;
  }> = personalInfo
    ? Object.entries(personalInfo).map(([question, answer], index) => ({
        question,
        answer: answer as string,
        question_id: index + 1,
      }))
    : Array.isArray(activeQuiz.info)
    ? activeQuiz.info
    : [];

  // Convert quizAnswers to indices for the PDF
  // Personality-only answers mapped to indices against QUESTIONS (main quiz)
  // Unmatched answers resolve to -1 (skipped downstream) rather than 0, so a legacy
  // 17-answer record cannot render as 17 confident wrong answers. ClickUp 86bb0fwu5.
  const resolvedAnswers = resolveStoredAnswers(
    quizAnswers.slice(0, QUESTIONS.length)
  );
  const answersAsIndices = toRenderableIndices(resolvedAnswers);

  // All possible main categories - ensure all are included even if 0%
  const ALL_MAIN_CATEGORIES = Object.keys(CATEGORY_COLORS);
  const completeMainCategoryScores = ALL_MAIN_CATEGORIES.reduce(
    (acc, category) => {
      acc[category] = mainResults[category] ?? 0;
      return acc;
    },
    {} as Record<string, number>
  );

  // Mapping from main categories to their specific sub-types
  const MAIN_TO_SPECIFIC_MAPPING: Record<string, string[]> = {
    "Dream Chaser": ["Visionary Investor", "Powerhouse Investor"],
    "Safety Seeker": ["Cautious Saver", "Responsible Saver"],
    "Wealth Architect": ["Analytic Investor", "Ambitious Builder"],
    "Generous Altruist": ["Purpose-Driven Provider", "Empathetic Giver"],
    "Freedom Finder": ["Lifestyle Spender", "Spontaneous Spender"],
  };

  // Resolve ties/mismatches: if the specific dominant subtype does not belong to the
  // main dominant category (tie case), derive the main category from the specific result.
  const INVERSE_SPECIFIC_TO_MAIN: Record<string, string> = Object.entries(
    MAIN_TO_SPECIFIC_MAPPING
  ).reduce((acc, [main, specifics]) => {
    specifics.forEach((spec) => {
      acc[spec] = main;
    });
    return acc;
  }, {} as Record<string, string>);

  if (
    specificDominantCategory &&
    MAIN_TO_SPECIFIC_MAPPING[dominantCategory] &&
    !MAIN_TO_SPECIFIC_MAPPING[dominantCategory].includes(
      specificDominantCategory
    )
  ) {
    const inferredMain = INVERSE_SPECIFIC_TO_MAIN[specificDominantCategory];
    if (inferredMain) {
      dominantCategory = inferredMain;
    }
  }

  // Get the specific categories for the current dominant category
  const RELEVANT_SPECIFIC_CATEGORIES =
    MAIN_TO_SPECIFIC_MAPPING[dominantCategory] || [];

  // Only show specific categories relevant to the dominant category
  const relevantSpecificCategoryScores = RELEVANT_SPECIFIC_CATEGORIES.reduce(
    (acc, category) => {
      acc[category] = specificResults[category] ?? 0;
      return acc;
    },
    {} as Record<string, number>
  );
  console.log("relevant specific", relevantSpecificCategoryScores);

  // Sort categories by percentage (descending)
  const sortedCategories = ALL_MAIN_CATEGORIES.sort(
    (a, b) =>
      (completeMainCategoryScores[b] ?? 0) -
      (completeMainCategoryScores[a] ?? 0)
  );

  // For advisor view, there's no paywall
  const showPaywall = false;
  const handleUnlock = () => {};

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingTop: isMobile ? "100px" : 0,
        paddingLeft: isMobile ? "4px" : spacing.xl,
        paddingRight: isMobile ? "4px" : spacing.xl,
        marginBottom: spacing.xxl,
      }}
    >
      {/* Exercise Modal */}
      <BaseModal
        isVisible={selectedExercise !== null}
        onClose={() => setSelectedExercise(null)}
        title=""
      >
        {selectedExercise && (
          <View
            style={{
              maxWidth: 900,
              maxHeight: "80vh",
              overflowY: "auto",
              padding: isMobile ? 20 : 32,
              backgroundColor: colors.white,
              borderRadius: 16,
            }}
          >
            <ExerciseRenderer
              exercise={selectedExercise}
              savedData={
                exercisesData?.exercises[selectedExercise.id.toString()]
              }
              onComplete={(responses) => {
                console.log("Exercise completed:", responses);
              }}
            />
          </View>
        )}
      </BaseModal>

      {/* Action Row - Buttons and Tabs */}
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 16,
          marginTop: isMobile ? "0px" : "20px",
          marginBottom: 24,
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          maxWidth: 1400,
        }}
      >
        {/* Left side - Buttons */}
        <View style={{ display: "flex", flexDirection: "row", gap: 16 }}>
          {showDownload && currentActiveTab === "results" ? (
            <View style={{ position: "relative" }}>
              <Pressable
                onPress={() => setShowReportMenu((v) => !v)}
                style={{
                  backgroundColor: colors.dark,
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  border: "none",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <CustomText
                  style={{
                    color: colors.white,
                    fontWeight: 600,
                    fontSize: 16,
                    fontFamily: "Nunito",
                  }}
                >
                  Create Report ▾
                </CustomText>
              </Pressable>
              {showReportMenu && (
                <View
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: 4,
                    backgroundColor: colors.white,
                    borderRadius: 8,
                    border: `1px solid ${colors.gray[200]}`,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    zIndex: 100,
                    minWidth: 260,
                    overflow: "hidden",
                  }}
                >
                  <PDFDownloadLink
                    document={
                      <PersonalityTestPDF
                        answers={answersAsIndices}
                        categoryScores={categoryScores}
                        dominantCategory={dominantCategory}
                        personalInfo={userInfo.reduce((acc, curr) => {
                          acc[curr.question] = curr.answer;
                          return acc;
                        }, {} as Record<string, string>)}
                        exercises={workbookExercises}
                        userName={userName || ""}
                      />
                    }
                    fileName={`personality-detail-${new Date().toISOString().split("T")[0]}.pdf`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {({ loading }) => (
                      <Pressable
                        onPress={() => setShowReportMenu(false)}
                        style={{
                          padding: 14,
                          cursor: "pointer",
                          borderBottom: `1px solid ${colors.gray[100]}`,
                        }}
                      >
                        <CustomText
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: colors.dark,
                            fontFamily: "Nunito",
                            marginBottom: 2,
                          }}
                        >
                          {loading ? "Generating..." : "Personality Detail"}
                        </CustomText>
                        <CustomText
                          style={{
                            fontSize: 12,
                            color: colors.gray[500],
                            fontFamily: "Nunito",
                          }}
                        >
                          Full report with scores, traits, and exercises
                        </CustomText>
                      </Pressable>
                    )}
                  </PDFDownloadLink>
                  <PDFDownloadLink
                    document={
                      <PersonalityTestPDF
                        answers={answersAsIndices}
                        categoryScores={categoryScores}
                        dominantCategory={dominantCategory}
                        personalInfo={userInfo.reduce((acc, curr) => {
                          acc[curr.question] = curr.answer;
                          return acc;
                        }, {} as Record<string, string>)}
                        exercises={[]}
                        userName={userName || ""}
                        overview
                      />
                    }
                    fileName={`personality-overview-${new Date().toISOString().split("T")[0]}.pdf`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {({ loading }) => (
                      <Pressable
                        onPress={() => setShowReportMenu(false)}
                        style={{
                          padding: 14,
                          cursor: "pointer",
                        }}
                      >
                        <CustomText
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: colors.dark,
                            fontFamily: "Nunito",
                            marginBottom: 2,
                          }}
                        >
                          {loading ? "Generating..." : "Personality Overview"}
                        </CustomText>
                        <CustomText
                          style={{
                            fontSize: 12,
                            color: colors.gray[500],
                            fontFamily: "Nunito",
                          }}
                        >
                          Summary with type and score breakdown
                        </CustomText>
                      </Pressable>
                    )}
                  </PDFDownloadLink>
                </View>
              )}
            </View>
          ) : currentActiveTab === "results" ? (
            <>
              <View
                style={{ position: "relative" }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Pressable
                  onPress={() => {
                    // Navigate to InfoCollection for profile completion
                    navigate("/client/info-collection", {
                      state: {
                        from: "personality-tab",
                        quizId: activeQuiz.id,
                        email: profile?.email,
                        sfAccount: sfAccount,
                      },
                    });
                  }}
                  style={{
                    backgroundColor: colors.primary,
                    opacity: isHovered ? 0.7 : 1,
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    borderRadius: 16,
                    cursor: "pointer",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    fontSize: 16,
                    fontFamily: "Nunito",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 2,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <CustomText
                    style={{
                      color: colors.white,
                      fontWeight: 600,
                      fontSize: 16,
                      fontFamily: "Nunito",
                    }}
                  >
                    Complete Profile
                  </CustomText>
                </Pressable>
                <View
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 16,
                    backgroundColor: "transparent",
                    zIndex: 1,
                    animation: "pulsate-primary 1.5s ease-out infinite",
                  }}
                />
              </View>
            </>
          ) : null}

          {/* Exercises Button - Only show in client view when on results tab */}
          {!isAdvisorView && currentActiveTab === "results" && setActiveTab && (
            <Pressable
              onPress={() => setActiveTab("exercises")}
              style={{
                backgroundColor: colors.primary,
                paddingTop: "12px",
                paddingBottom: "12px",
                paddingLeft: "12px",
                paddingRight: "12px",
                borderRadius: 8,
                cursor: "pointer",
                border: "none",
                color: colors.white,
                fontWeight: 600,
                fontSize: 16,
                fontFamily: "Nunito",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <SvgBrain color={colors.white} width={16} height={16} />
              <CustomText
                style={{
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: 16,
                  fontFamily: "Nunito",
                }}
              >
                View Exercises
              </CustomText>
            </Pressable>
          )}

          {/* Send Quiz Button - Only show in advisor view */}
          {isAdvisorView && (
            <Pressable
              onPress={() => setShowSendQuizModal(true)}
              style={{
                backgroundColor: colors.white,
                paddingTop: "12px",
                paddingBottom: "12px",
                paddingLeft: "16px",
                paddingRight: "16px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${colors.gray[300]}`,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <SvgClipboard color={colors.dark} width={16} height={16} />
              <CustomText
                style={{
                  color: colors.dark,
                  fontWeight: 600,
                  fontSize: 16,
                  fontFamily: "Nunito",
                }}
              >
                Send Quiz
              </CustomText>
            </Pressable>
          )}
        </View>

        {/* Right side - Tabs (Only show in advisor view) */}
        {isAdvisorView && (
          <View style={{ display: "flex", flexDirection: "row", gap: 24 }}>
            <Pressable
              style={{
                paddingVertical: 16,
                paddingHorizontal: 0,
                position: "relative",
              }}
              onPress={() => setInternalActiveTab("results")}
            >
              <CustomText
                style={{
                  color:
                    (currentActiveTab as string) === "results"
                      ? colors.primary
                      : colors.gray[600],
                  fontWeight: "600",
                  fontSize: 14,
                  fontFamily: "Nunito",
                }}
              >
                Results
              </CustomText>
              {currentActiveTab === "results" && (
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: colors.primary,
                  }}
                />
              )}
            </Pressable>

            <Pressable
              style={{
                paddingVertical: 16,
                paddingHorizontal: 0,
                position: "relative",
              }}
              onPress={() => setInternalActiveTab("exercises")}
            >
              <CustomText
                style={{
                  color:
                    (currentActiveTab as string) === "exercises"
                      ? colors.primary
                      : colors.gray[600],
                  fontWeight: "600",
                  fontSize: 14,
                  fontFamily: "Nunito",
                }}
              >
                Exercises
              </CustomText>
              {(currentActiveTab as string) === "exercises" && (
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: colors.primary,
                  }}
                />
              )}
            </Pressable>
          </View>
        )}
      </View>

      {isAdvisorView && (
        <SendQuizModal
          isVisible={showSendQuizModal}
          onClose={() => setShowSendQuizModal(false)}
          quizLink={`${window.location.origin}/quiz/${sfAccount || ''}`}
          clientName={userName}
        />
      )}

      {currentActiveTab === "results" ? (
        // ... existing results content ...
        <View style={{ width: "100%", maxWidth: 1400 }}>
          {/* Copy Success Toast */}
          {showCopiedToast && (
            <View
              style={{
                position: "fixed",
                bottom: "50px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#e8f5e8",
                color: "#2d5a2d",
                padding: "16px",
                borderRadius: 12,
                zIndex: 9999,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                fontFamily: "Nunito",
                fontSize: 16,
                fontWeight: 600,
                border: "1px solid #c8e6c8",
              }}
            >
              Quiz link copied to clipboard! 📋
            </View>
          )}

          {/* Info Modal */}
          <BaseModal
            isVisible={showInfoModal}
            onClose={() => setShowInfoModal(false)}
            title="Your Information"
          >
            <View style={{ maxHeight: 500, overflowY: "auto", minWidth: 600 }}>
              {userInfo.length > 0 ? (
                userInfo.map((item, idx) => (
                  <View
                    key={idx}
                    style={{
                      marginBottom: 32,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <View
                      style={{ flex: 1, maxWidth: "100%", paddingRight: 0 }}
                    >
                      <CustomText style={{ fontWeight: 600, marginBottom: 8 }}>
                        {item.question}
                      </CustomText>
                      <CustomText
                        style={{ color: colors.gray[700], marginBottom: 8 }}
                      >
                        {item.answer}
                      </CustomText>
                    </View>
                  </View>
                ))
              ) : (
                <View style={{ textAlign: "center", padding: 20 }}>
                  <CustomText style={{ color: colors.gray[500] }}>
                    No personal information available.
                  </CustomText>
                </View>
              )}
            </View>
          </BaseModal>

          {/* Answers Modal */}
          <BaseModal
            isVisible={showAnswersModal}
            onClose={() => setShowAnswersModal(false)}
            title="Your Quiz Answers"
          >
            <View style={{ maxHeight: 500, overflowY: "auto" }}>
              {/* Primary Personality Questions */}
              <CustomText
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  marginBottom: 18,
                  marginLeft: 0,
                  paddingLeft: 0,
                }}
              >
                Primary Personality Questions
              </CustomText>
              {quizAnswers.slice(0, quizAnswers.length - 5).map(
                (
                  item: {
                    question: string;
                    answer: string;
                    category: string;
                  },
                  idx: number
                ) => (
                  <View
                    key={idx}
                    style={{
                      marginBottom: 32,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <View
                      style={{ flex: 1, maxWidth: "100%", paddingRight: 0 }}
                    >
                      <CustomText style={{ fontWeight: 600, marginBottom: 8 }}>
                        {item.question}
                      </CustomText>
                      <CustomText
                        style={{ color: colors.gray[700], marginBottom: 8 }}
                      >
                        {item.answer}
                      </CustomText>
                    </View>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        minWidth: 100,
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          backgroundColor:
                            CATEGORY_COLORS[item.category] || colors.gray[400],
                          marginRight: 6,
                          border: "1.5px solid #e0e0e0",
                        }}
                      />
                      <CustomText
                        style={{ color: colors.gray[500], fontSize: 14 }}
                      >
                        {item.category}
                      </CustomText>
                    </View>
                  </View>
                )
              )}

              {/* Gray separator */}
              <div
                style={{
                  width: "100%",
                  height: 1,
                  background: colors.gray[300],
                  margin: "32px 0",
                }}
              />

              {/* The "Subset Questions" list was REMOVED on 2026-08-20 (ClickUp
                  86bb0f9vz). It rendered `quizAnswers.slice(-5)` — with the
                  sub-quiz gone that slice returns the last five MAIN answers, so
                  keeping it relabels real answers as sub-type ones. */}
            </View>
          </BaseModal>

          {/* Card for heading and subtitle */}
          <View
            style={{
              width: "100%",
              maxWidth: 1400,
              paddingBottom: isMobile ? spacing.sm : 0,
            }}
          >
            <View
              style={{
                backgroundColor: colors.white,
                borderRadius: 16,
                padding: isMobile ? spacing.md : spacing.xl,
                marginBottom: spacing.xl,
                width: "100%",
                alignItems: "center",
                border: "3px solid black",
              }}
            >
              <CustomText
                style={{
                  color: colors.dark,
                  textAlign: "center",
                  width: "100%",
                  fontFamily: "Nunito",
                  fontWeight: 400,
                  fontSize: isMobile ? 24 : 32,
                }}
              >
                You are primarily a
                {dominantCategory.toLowerCase().startsWith("a") ||
                dominantCategory.toLowerCase().startsWith("e") ||
                dominantCategory.toLowerCase().startsWith("i") ||
                dominantCategory.toLowerCase().startsWith("o") ||
                dominantCategory.toLowerCase().startsWith("u")
                  ? "n "
                  : ""}
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 55 : 120,
                  fontWeight: "700",
                  color: colors.dark,
                  textAlign: "center",
                  width: "100%",
                  fontFamily: "Nunito",
                }}
              >
                {dominantCategory}
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 24 : 32,
                  color: colors.gray[700],
                  textAlign: "center",
                  width: "100%",
                  marginBottom: isMobile ? spacing.xl : spacing.xxl,
                  fontFamily: "Nunito",
                }}
              >
                {coreSection.Subtext}
              </CustomText>
              {quizDateTaken && (
                <CustomText
                  style={{
                    fontSize: isMobile ? 14 : 16,
                    color: colors.gray[500],
                    textAlign: "center",
                    width: "100%",
                    marginTop: isMobile ? -spacing.md : -spacing.lg,
                    marginBottom: isMobile ? spacing.md : spacing.lg,
                    fontFamily: "Nunito",
                  }}
                >
                  Assessment completed {quizDateTaken}
                </CustomText>
              )}
              <CustomText
                style={{
                  fontSize: isMobile ? 18 : 24,
                  color: colors.gray[700],
                  lineHeight: isMobile ? 24 : 28,
                  marginBottom: isMobile ? spacing.md : spacing.lg,
                  fontFamily: "Nunito",
                  textAlign: "center",
                  width: "90%",
                }}
              >
                {coreSection.Overview}
              </CustomText>

              {sfAccount && (
                <View
                  style={{
                    backgroundColor: colors.green + "20",
                    padding: isMobile ? spacing.xs : spacing.sm,
                    paddingLeft: isMobile ? spacing.md : spacing.lg,
                    paddingRight: isMobile ? spacing.md : spacing.lg,
                    borderRadius: 8,
                    marginTop: isMobile ? 8 : 8,
                    alignSelf: "center",
                  }}
                >
                  <CustomText
                    style={{
                      color: colors.green,
                      textAlign: "center",
                      whiteSpace: isMobile ? "normal" : "nowrap",
                      fontSize: isMobile ? 18 : 24,
                      fontFamily: "Nunito",
                    }}
                  >
                    Your advisor has received your results!
                  </CustomText>
                </View>
              )}
            </View>

            {/* <View style={{
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: spacing.xl,
            }}>
              {!hasPaid && (
                <button
                  style={{
                    background: hasPaid ? colors.success : "#222",
                    color: "#fff",
                    border: "none",
                    borderRadius: 100,
                    padding: "10px 32px",
                    fontFamily: 'Nunito',
                    fontSize: isMobile ? 18 : 24,
                    fontWeight: 500,
                    cursor: "pointer",
                    marginRight: 8,
                    width: '50%',
                  }}
                  onClick={() => {
                    if (hasPaid) {
                      // User has already paid - show download or navigate to specific quiz
                      const specificQuizData = {
                        ...resultsData,
                        sfAccount,
                        quizId,
                        from: location.pathname
                      };
                              
                      if (sfAccount) {
                        navigate(`/quiz/specific/confirm/${sfAccount}`, { state: specificQuizData });
                      } else {
                        navigate('/quiz/specific/confirm', { state: specificQuizData });
                      }
                    } else {
                      // User hasn't paid - navigate to information collection page
                      navigate('/quiz/results/informationform', { state: { ...resultsData, from: location.pathname}});
                    }
                  }}
                >
                  Download Your Results!
                </button>
              )}
            </View> */}

            {/* Card for main quiz graph results */}
            <View
              style={{
                backgroundColor: colors.white,
                borderRadius: 16,
                padding: isMobile ? spacing.md : spacing.xl,
                marginBottom: spacing.xl,
                width: "100%",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-around",
                border: "3px solid black",
              }}
            >
              {/* Each category and their relative contributions */}
              <View
                style={{
                  width: isMobile ? "100%" : "50%",
                  padding: isMobile ? spacing.md : spacing.xl,
                }}
              >
                {sortedCategories.map((category) => (
                  <View key={category} style={{ marginBottom: 24 }}>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <CustomText
                        style={{ fontFamily: "Nunito", fontSize: 24 }}
                      >
                        {category}
                      </CustomText>
                      <CustomText
                        style={{
                          color: "#717171",
                          fontWeight: 400,
                          fontSize: 18,
                        }}
                      >
                        {Math.round(completeMainCategoryScores[category])}%
                      </CustomText>
                    </View>
                    <CatContributionBar
                      value={Math.round(completeMainCategoryScores[category])}
                      color={CATEGORY_COLORS[category] || colors.primary}
                    />
                  </View>
                ))}
              </View>
              {/* The pie chart graph of all categories */}
              <View
                style={{
                  width: isMobile ? "100%" : "50%",
                  justifyContent: "center",
                }}
              >
                <ResultsGraph
                  graphData={completeMainCategoryScores}
                  type="main"
                />
              </View>
            </View>

            {/* Detailed Personality Analysis Section */}
            <View
              style={{
                width: "100%",
                margin: "0 auto",
              }}
            >
              {/* Traits Section */}
              <View
                style={{
                  width: "100%",
                  margin: "0 auto",
                  marginBottom: spacing.xl,
                  backgroundColor: colors.white,
                  borderRadius: 16,
                  paddingHorizontal: isMobile ? spacing.lg : spacing.xxl,
                  paddingVertical: isMobile ? spacing.md : spacing.lg,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  justifyContent: "space-between",
                  border: "3px solid black",
                }}
              >
                <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: spacing.sm,
                      marginBottom: isMobile ? spacing.sm : spacing.md,
                    }}
                  >
                    <CustomText
                      style={{
                        fontSize: isMobile ? 36 : 48,
                        fontWeight: "700",
                        color: colors.dark,
                        fontFamily: "Nunito",
                        textAlign: "center",
                      }}
                    >
                      Dominant traits
                    </CustomText>
                  </View>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: spacing.sm,
                      marginBottom: isMobile ? spacing.md : spacing.lg,
                      width: "100%",
                    }}
                  >
                    {coreSection.DominantTraits.map((trait, i) => (
                      <View
                        key={i}
                        style={{
                          backgroundColor: colors.gray[100],
                          paddingHorizontal: spacing.sm,
                          paddingVertical: spacing.xs,
                          borderRadius: 4,
                          marginBottom: spacing.xs,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <CustomText
                          numberOfLines={isMobile ? 0 : 1}
                          style={{
                            fontSize: isMobile ? 18 : 24,
                            color: colors.gray[700],
                            fontFamily: "Nunito",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {trait}
                        </CustomText>
                      </View>
                    ))}
                  </View>
                </View>
              </View>

              {/* Strengths, Weaknesses, and Sub-Personality Section */}
              <View
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "center" : "stretch",
                  width: "100%",
                  gap: isMobile ? 0 : spacing.md,
                  margin: "0 auto",
                  marginBottom: isMobile ? spacing.sm : spacing.md,
                }}
              >
                {/* Left half is strengths and weaknesses */}
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* Strengths Section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "#ebf7ed",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid #b1ddb4",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          ✅ Strengths
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {coreSection.Strengths.map((strength, i) => (
                          <View
                            key={i}
                            style={{
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 10,
                              color: colors.success,
                            }}
                          >
                            <CustomText
                              style={{
                                alignSelf: "flex-start",
                                paddingTop: 5,
                                fontSize: isMobile ? 30 : 26,
                                color: colors.success,
                              }}
                            >
                              &#x2713;
                            </CustomText>
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.success,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {strength}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>

                  {/* Challenges Section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "#fffbe8",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid #f4e67d",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          ⚠️ Weaknesses
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {coreSection.Weaknesses.map((challenge, i) => (
                          <View
                            key={i}
                            style={{
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 10,
                              color: colors.danger,
                            }}
                          >
                            <View
                              style={{ alignSelf: "flex-start", paddingTop: 5 }}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.4449 0.608765C8.0183 -0.107015 6.9817 -0.107015 6.55509 0.608766L0.161178 11.3368C-0.275824 12.07 0.252503 13 1.10608 13H13.8939C14.7475 13 15.2758 12.07 14.8388 11.3368L8.4449 0.608765ZM7.4141 1.12073C7.45288 1.05566 7.54712 1.05566 7.5859 1.12073L13.9798 11.8488C14.0196 11.9154 13.9715 12 13.8939 12H1.10608C1.02849 12 0.980454 11.9154 1.02018 11.8488L7.4141 1.12073ZM6.8269 4.48611C6.81221 4.10423 7.11783 3.78663 7.5 3.78663C7.88217 3.78663 8.18778 4.10423 8.1731 4.48612L8.01921 8.48701C8.00848 8.766 7.7792 8.98664 7.5 8.98664C7.2208 8.98664 6.99151 8.766 6.98078 8.48701L6.8269 4.48611ZM8.24989 10.476C8.24989 10.8902 7.9141 11.226 7.49989 11.226C7.08567 11.226 6.74989 10.8902 6.74989 10.476C6.74989 10.0618 7.08567 9.72599 7.49989 9.72599C7.9141 9.72599 8.24989 10.0618 8.24989 10.476Z"
                                  fill="currentColor"
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                ></path>
                              </svg>
                            </View>
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.danger,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {challenge}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                </View>

                {/* The sub-personality analysis panel (subset graph, "You are a
                    sub-type!", contribution bars, sub-type definition) was
                    REMOVED on 2026-08-20 — ClickUp 86bb0f9vz. */}
              </View>

              {/* Famous person, growth opportunities, and behavioral concepts sections */}
              <View
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "center" : "stretch",
                  width: "100%",
                  gap: isMobile ? 0 : spacing.md,
                  margin: "0 auto",
                }}
              >
                {/* Right half is growth opportunities and behavioral concepts */}
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {/* Growth opportunities section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "white",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid black",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          🌱 Growth Opportunities
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {coreSection.GrowthOpportunities.map((challenge, i) => (
                          <View
                            key={i}
                            style={{
                              backgroundColor: colors.green + "20",
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                            }}
                          >
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.green,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {challenge}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                  {/* Behavioral concepts section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "white",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid black",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          🧠 Behavioral Concepts
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {coreSection.BehavioralConcepts.map((challenge, i) => (
                          <View
                            key={i}
                            style={{
                              backgroundColor: colors.pink + "20",
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                            }}
                          >
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.pink,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {challenge}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      ) : (
        // Exercises Tab
        <View style={{ width: "100%", maxWidth: 1400 }}>
          <CustomText
            style={{
              fontSize: isMobile ? 32 : 48,
              fontWeight: 700,
              color: colors.dark,
              textAlign: "center",
              marginBottom: 32,
              fontFamily: "Nunito",
            }}
          >
            Personalized Exercises
          </CustomText>

          <View
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {workbookExercises.map((exercise, idx) => {
              const {
                type,
                icon: ExerciseIcon,
                color,
              } = getExerciseTypeAndIcon(exercise.id);
              const exerciseProgress =
                exercisesData?.exercises[exercise.id.toString()];
              const hasProgress =
                exerciseProgress &&
                Object.keys(exerciseProgress.responses).length > 0;

              return (
                <Pressable
                  key={exercise.id}
                  onPress={() => setSelectedExercise(exercise)}
                  style={{
                    // A colorful box per activity (McKayla 2026-10-12, "like the
                    // example layout I sent you").
                    backgroundColor: `${EXERCISE_BOX_COLORS[idx % EXERCISE_BOX_COLORS.length]}1F`,
                    padding: 24,
                    borderRadius: 16,
                    border: hasProgress
                      ? `2px solid ${colors.success}`
                      : `2px solid ${EXERCISE_BOX_COLORS[idx % EXERCISE_BOX_COLORS.length]}`,
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    ":hover": {
                      transform: "translateY(-2px)",
                    },
                    position: "relative",
                  }}
                >
                  <CustomText
                    style={{
                      fontSize: isMobile ? 20 : 24,
                      fontWeight: 700,
                      color: colors.dark,
                      marginBottom: 8,
                      fontFamily: "Nunito",
                    }}
                  >
                    {exercise.title}
                  </CustomText>

                  <View
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "flex-start",
                      gap: isMobile ? 12 : 16,
                    }}
                  >
                    <CustomText
                      style={{
                        fontSize: isMobile ? 16 : 18,
                        color: colors.gray[700],
                        fontFamily: "Nunito",
                        lineHeight: isMobile ? 24 : 28,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        flex: 1,
                      }}
                    >
                      {exercise.intro}
                    </CustomText>

                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: "#f9fafb",
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 8,
                        alignSelf: "flex-start",
                        flexShrink: 0,
                      }}
                    >
                      <ExerciseIcon width={24} height={24} color={color} />
                      <CustomText
                        style={{
                          fontSize: isMobile ? 18 : 20,
                          fontWeight: 700,
                          color: colors.dark,
                          marginLeft: 8,
                          fontFamily: "Nunito",
                        }}
                      >
                        {type}
                      </CustomText>
                    </View>
                  </View>

                  {/* Progress indicator */}
                  {hasProgress && (
                    <View
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: colors.success,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 4,
                      }}
                    >
                      <CustomText
                        style={{
                          color: "white",
                          fontSize: 12,
                          fontWeight: "600",
                          fontFamily: "Nunito",
                        }}
                      >
                        Saved
                      </CustomText>
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}
